import React, { useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import clsx from "clsx";
import { Badge, Button } from "antd";
import { useSearchParams } from "react-router";
import Table from "../../../Common/Table";
import { DatePicker } from "antd";
import InboxViewModal from "./View";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  NotificationType,
  UserNotificationStatus,
  UserNotificationTableRow,
  useUserNotificationTableGet,
  useNotificationMarkRead,
  useNotificationMakUnRead,
  useNotificationMarkReadAll,
} from "../../../../hooks/api/notifications";
import { RequestData } from "@ant-design/pro-components";
import { SortOrder } from "antd/es/table/interface";
import { queryFilterParams, getSorterParams } from "../../../../utils";
import { dateTimeFormat } from "../../../../contstant";

export const badgeColors: Record<UserNotificationStatus, string> = {
  [UserNotificationStatus.UNREAD]: "red",
  [UserNotificationStatus.READ]: "green",
};

export const UserNotificationStatusLabels: Record<
  UserNotificationStatus,
  string
> = {
  [UserNotificationStatus.UNREAD]: "Unread",
  [UserNotificationStatus.READ]: "Read",
};

function ToolBar({ onReload }: { onReload: () => void }) {
  const { t } = useTranslation();
  const notificationMarkReadAll = useNotificationMarkReadAll();

  // Marking everything as read is the one bulk action the list offers. The
  // reverse — mark all as unread — was removed at the designer's request; its
  // hook, useNotificationMarkUnReadAll, is left untouched in hooks/api and now
  // has no caller. See DEVELOPER_TASKS.md.
  return (
    <Button
      onClick={async () => {
        await notificationMarkReadAll.fetch();
        onReload();
      }}
    >
      {t("pages.notificationCenter.markAllAsRead")}
    </Button>
  );
}

export default function Inbox(): React.ReactNode | null {
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<any>(null);
  const { t, i18n } = useTranslation();
  const userNotificationTableGet = useUserNotificationTableGet();
  const notificationMarkRead = useNotificationMarkRead();
  const notificationMakUnRead = useNotificationMakUnRead();

  const columns: ProColumns[] = [
    {
      title: t("pages.notificationCenter.type"),
      dataIndex: "notificationType",
      key: "notificationType",
      sorter: true,
      valueType: "select",
      valueEnum: NotificationType,
      render: (_, record) => (
        <span className="capitalize-first-letter">
          {record.notification.notificationType}
        </span>
      ),
    },
    {
      title: t("pages.notificationCenter.title"),
      dataIndex: ["notification", "title"],
      key: "title",
      hideInSearch: true,
    },
    {
      title: t("pages.notificationCenter.message"),
      dataIndex: ["notification", "description"],
      key: "message",
      hideInSearch: true,
      width: 185,
      ellipsis: true,
    },
    {
      title: t("pages.notificationCenter.sentAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      hideInSearch: true,
      renderText: (text) => dayjs(text).format(dateTimeFormat),
    },
    {
      title: t("pages.notificationCenter.sentAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      valueType: "dateRange",
      hideInTable: true,
      search: {
        transform: (value?: [string, string]) => {
          if (!value || value.length !== 2) return {};

          return {
            createdFrom: value[0],
            createdTo: value[1],
          };
        },
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      valueType: "select",
      valueEnum: UserNotificationStatusLabels,
      render: (_, record: { status: UserNotificationStatus }) => (
        <Badge
          color={badgeColors[record.status]}
          text={UserNotificationStatusLabels[record.status]}
        />
      ),
      width: 120,
    },
  ];

  const onToggleReadStatus = async (
    id: string,
    status: UserNotificationStatus,
  ) => {
    try {
      const isUnread = status === UserNotificationStatus.UNREAD;

      const response = isUnread
        ? await notificationMarkRead.fetch(undefined, `${id}/read`)
        : await notificationMakUnRead.fetch(undefined, `${id}/unread`);

      if (response) {
        setTableKey(Date.now());

        // The panel stays open: marking a notification read is a change to the
        // row, not a reason to close what is being read. Its own copy of the
        // record is updated in place so the button flips to the opposite
        // action instead of going stale.
        setCurrent((previous: UserNotificationTableRow | null) =>
          previous && previous.id === id
            ? {
                ...previous,
                status: isUnread
                  ? UserNotificationStatus.READ
                  : UserNotificationStatus.UNREAD,
              }
            : previous,
        );
      }
    } catch (error) {
      console.error("Failed to update notification status", error);
    }
  };
  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<UserNotificationTableRow>>> => {
    const mappedSorter: Record<string, SortOrder> = {};

    Object.entries(sorter).forEach(([key, value]) => {
      if (key === "notificationType") {
        mappedSorter["notificationType"] = value;
      } else {
        mappedSorter[key] = value;
      }
    });

    const newParams = queryFilterParams({
      page: current ? `${current}` : "1",
      pageSize: pageSize ? `${pageSize}` : "10",
      ...args,
      ...getSorterParams(mappedSorter),
    });

    setSearchParams(
      { ...args, ...getSorterParams(mappedSorter) },
      { replace: true },
    );

    return userNotificationTableGet
      .fetch({
        ...newParams,
        name: newParams?.name || undefined,
      })
      .then((response) => {
        if (response) {
          const { data, total } = response;

          return {
            data: (data || []).map((item) => ({
              ...item,
              key: item.id,
            })),
            success: true,
            total,
          };
        }

        return { data: [], success: false, total: 0 };
      });
  };

  return (
    <>
      <Table
        key={`${tableKey}-${i18n.language}`}
        resetKey={() => setTableKey(Date.now())}
        className={clsx("transparent")}
        headerTitle={t("pages.notificationCenter.notificationsList")}
        toolBarRender={() => [
          <ToolBar key="toolbar" onReload={() => setTableKey(Date.now())} />,
        ]}
        request={tableRequest}
        columns={columns}
        actions={[
          [
            ({ record }: { record: any }) => {
              const isUnread = record.status === "UNREAD";

              return (
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleReadStatus(record.id, record.status);
                  }}
                >
                  {isUnread
                    ? t("pages.notificationCenter.markAsRead")
                    : t("pages.notificationCenter.markAsUnread")}
                </Button>
              );
            },
            {},
          ],
        ]}
        rowClassName="cursor-pointer"
        onRow={(record) => ({
          onClick: () => {
            setCurrent(record);
            setOpen(true);
          },
        })}
        search={{
          style: {
            // display: 'none',
          },
        }}
        options={{
          search: {
            name: "name",
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-notification",
          persistenceType: "localStorage",
        }}
      />
      <InboxViewModal
        open={open}
        onClose={() => setOpen(false)}
        data={current}
        onMarkAsRead={onToggleReadStatus}
      />
    </>
  );
}
